import {
  Expand,
  History,
  Pause,
  Play,
  Radio,
  Rewind,
  FastForward,
  GanttChart,
  Shrink,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';

import type { Device } from '../inventory/api';
import { atWallClock, clock, dayOf } from '../playback/time';
import { DeviceSyncTile } from '../wall/DeviceSyncTile';
import { MosaicTimeline } from '../wall/MosaicTimeline';
import { SyncTile } from '../wall/SyncTile';
import {
  clockTime,
  LIVE_EDGE_MS,
  RATES,
  rebase,
  REPLAY_MINUTES,
  startClock,
  type Clock,
  type ReplaySource,
} from '../wall/sync';
import type { Grid } from './layouts';
import { Mosaic } from './Mosaic';
import { gridCols } from './tiles';
/**
 * Live, or a replay on the shared clock. `epoch` is the clock's last jump
 * (start, seek or resume): device streams reopen from there.
 */
type Mode = { kind: 'live' } | { kind: 'replay'; clock: Clock; from: number; epoch: number };

/** Now, on the monotonic clock (for the replay) and the wall clock. */
function stamp() {
  return { perf: performance.now(), wall: Date.now() };
}

/** The control bar hides after this long without the mouse, in full screen. */
const IDLE_MS = 3000;

export interface MosaicViewerProps {
  name: string;
  grid: Grid;
  slots: (string | null)[];
  devices: Map<string, Device>;
  cameras: { id: string; name: string }[];
  canReplay: boolean;
  active: boolean;
  timeZone: string | undefined;
  /**
   * In full screen the control bar normally floats over the picture and
   * fades while the mouse is still. The monitoring panel keeps it docked and
   * visible (0.68.3): the operator works the timeline in full screen.
   */
  dockedBarInFullscreen?: boolean;
  /**
   * The bar's own "Tela cheia" button. The monitoring panel hides it (0.68.4):
   * its sidebar already has one, and the bar keeps to a single line.
   */
  fullscreenButton?: boolean;
}

/**
 * The mosaic viewer: the live grid or a synchronised replay of every camera,
 * the timeline and the control bar. The caller resolves the layout and devices.
 */
export function MosaicViewer({
  name,
  grid,
  slots,
  devices: byId,
  cameras,
  canReplay,
  active,
  timeZone,
  dockedBarInFullscreen = false,
  fullscreenButton = true,
}: MosaicViewerProps) {
  const [mode, setMode] = useState<Mode>({ kind: 'live' });
  const [origin, setOrigin] = useState<ReplaySource>('server');
  const [selected, setSelected] = useState(0);
  const [maximized, setMaximized] = useState<number | null>(null);
  // The last tick: monotonic time for the replay clock, wall time for "now".
  const [tick, setTick] = useState(stamp);
  const [fullscreen, setFullscreen] = useState(() => Boolean(document.fullscreenElement));
  const [idle, setIdle] = useState(false);
  // The recordings timeline under the grid, and the day it shows.
  const [showTimeline, setShowTimeline] = useState(true);
  const [day, setDay] = useState(() => dayOf(Date.now(), timeZone));

  const replay = mode.kind === 'replay' ? mode : null;
  const t = replay ? clockTime(replay.clock, tick.perf) : tick.wall;

  // The replay clock ticks four times a second; live needs no clock. Having
  // caught up with the present, the window goes back to live.
  useEffect(() => {
    if (!replay) return;
    const timer = window.setInterval(() => {
      const now = stamp();
      if (clockTime(replay.clock, now.perf) >= now.wall - LIVE_EDGE_MS) setMode({ kind: 'live' });
      else setTick(now);
    }, 250);
    return () => {
      window.clearInterval(timer);
    };
  }, [replay]);

  useEffect(() => {
    const onChange = () => {
      setFullscreen(Boolean(document.fullscreenElement));
      setIdle(false);
    };
    document.addEventListener('fullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
    };
  }, []);

  // In full screen the bar leaves the picture alone until the mouse moves.
  useEffect(() => {
    if (!fullscreen || dockedBarInFullscreen) return;
    let timer = window.setTimeout(() => {
      setIdle(true);
    }, IDLE_MS);
    const onMove = () => {
      setIdle(false);
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        setIdle(true);
      }, IDLE_MS);
    };
    window.addEventListener('mousemove', onMove);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('mousemove', onMove);
    };
  }, [fullscreen, dockedBarInFullscreen]);

  const startReplay = (from: number) => {
    const now = stamp();
    setTick(now);
    setDay(dayOf(from, timeZone));
    setMode({ kind: 'replay', clock: startClock(from, now.perf), from, epoch: from });
  };
  const adjust = (patch: Parameters<typeof rebase>[2]) => {
    if (!replay) return;
    const now = stamp();
    setTick(now);
    const next = rebase(replay.clock, now.perf, patch);
    // A seek or a resume is a jump: device streams reopen from there.
    const jump = patch.base !== undefined || (patch.playing === true && !replay.clock.playing);
    setMode({ ...replay, clock: next, epoch: jump ? next.base : replay.epoch });
  };
  // A click on the timeline: every camera from t, from that track's origin.
  const seekTo = (at: number, o: ReplaySource) => {
    setOrigin(o);
    if (at >= Date.now() - LIVE_EDGE_MS) {
      setMode({ kind: 'live' });
      return;
    }
    if (!replay) {
      startReplay(at);
      return;
    }
    adjust({ base: at, playing: true, ...(o === 'device' ? { rate: 1 as const } : {}) });
  };
  const toggleFullscreen = () => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void document.documentElement.requestFullscreen().catch(() => undefined);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target;
      if (el instanceof HTMLInputElement || el instanceof HTMLSelectElement) return;
      if (e.key === 'f') toggleFullscreen();
      if (!replay) return;
      if (e.key === ' ' || e.key === 'k') {
        e.preventDefault();
        adjust({ playing: !replay.clock.playing });
      } else if (e.key === 'j' || e.key === 'l') {
        adjust({
          base: clockTime(replay.clock, performance.now()) + (e.key === 'j' ? -10_000 : 10_000),
        });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  });

  const positions = maximized === null ? Array.from({ length: grid }, (_, i) => i) : [maximized];

  return (
    <div className="flex h-full flex-col bg-black">
      {/* On phones the grid keeps the layout (2×2, 3×3…) at 16:9, as large as
          fits, instead of stretching tiles into slivers. */}
      <main
        className="relative min-h-0 flex-1 max-md:grid max-md:place-items-center max-md:[container-type:size]"
        aria-label={name}
      >
        <div
          data-testid="mosaic-frame"
          className="size-full max-md:aspect-video max-md:h-auto max-md:w-[min(100cqw,100cqh*16/9)]"
        >
          {replay ? (
            <div
              className={`grid size-full gap-px bg-line ${gridCols[maximized === null ? grid : 1]}`}
            >
              {positions.map((position) => {
                const deviceId = slots[position] ?? null;
                const label = deviceId ? (byId.get(deviceId)?.name ?? 'Câmera indisponível') : '';
                return (
                  <div
                    key={position}
                    className="relative min-h-0 min-w-0 bg-canvas"
                    onDoubleClick={() => {
                      if (deviceId) setMaximized((m) => (m === null ? position : null));
                    }}
                  >
                    {deviceId && byId.has(deviceId) && origin === 'device' && (
                      <DeviceSyncTile
                        deviceId={deviceId}
                        name={label}
                        t={t}
                        epoch={replay.epoch}
                        playing={replay.clock.playing}
                      />
                    )}
                    {deviceId && byId.has(deviceId) && origin === 'server' && (
                      <SyncTile
                        deviceId={deviceId}
                        t={t}
                        now={tick.wall}
                        playing={replay.clock.playing}
                        rate={replay.clock.rate}
                        replayFrom={replay.from}
                      />
                    )}
                    {deviceId && (
                      <span className="pointer-events-none absolute inset-x-0 bottom-0 truncate bg-linear-to-t from-black/80 to-transparent px-2 pt-4 pb-1.5 text-2xs font-medium text-white">
                        {label}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <Mosaic
              grid={grid}
              slots={slots}
              devices={byId}
              selected={selected}
              maximized={maximized}
              active={active}
              overrides={{}}
              readOnly
              onSelect={setSelected}
              onToggleMaximize={(p) => {
                setMaximized((m) => (m === p ? null : p));
              }}
              onRemove={() => undefined}
              onAssign={() => undefined}
              onSwap={() => undefined}
            />
          )}
        </div>
      </main>

      <div
        className={`border-t border-line bg-surface transition-opacity ${
          fullscreen && !dockedBarInFullscreen ? 'absolute inset-x-0 bottom-0' : ''
        } ${fullscreen && idle && !dockedBarInFullscreen ? 'pointer-events-none opacity-0' : 'opacity-100'}`}
      >
        {canReplay && showTimeline && cameras.length > 0 && (
          <MosaicTimeline
            cameras={cameras}
            day={day}
            onDay={setDay}
            playhead={replay ? t : null}
            timeZone={timeZone}
            onSeek={seekTo}
          />
        )}
        <div
          role="toolbar"
          aria-label="Controles do mosaico"
          className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-1.5 text-[13px] text-fg max-md:flex-nowrap max-md:overflow-x-auto max-md:overscroll-x-contain max-md:whitespace-nowrap max-md:[scrollbar-width:thin]"
        >
          <span className="min-w-0 truncate font-medium max-md:max-w-[10rem] max-md:shrink-0">
            {name}
          </span>
          {/* Always in the same place: which mode is on, and the way to the other. */}
          <div
            role="radiogroup"
            aria-label="Modo"
            className="flex items-center gap-0.5 rounded-md border border-line bg-canvas p-0.5"
          >
            <ModeButton
              active={!replay}
              tone="live"
              onClick={() => {
                setMode({ kind: 'live' });
              }}
            >
              <Radio aria-hidden="true" className="size-3.5" />
              Ao vivo
            </ModeButton>
            {canReplay && (
              <ModeButton
                active={replay !== null}
                tone="replay"
                title="Reproduzir a partir de 5 minutos atrás"
                onClick={() => {
                  if (!replay) startReplay(Date.now() - 5 * 60_000);
                }}
              >
                <History aria-hidden="true" className="size-3.5" />
                Reprodução
              </ModeButton>
            )}
          </div>
          {canReplay && (
            // Reserved width, so the controls after it never move.
            <span className="w-36 font-mono text-xs whitespace-nowrap text-warn tabular-nums">
              {replay
                ? `${origin === 'device' ? '≈ ' : ''}${dayLabel(t, timeZone)} ${clock(t, timeZone)}`
                : ''}
            </span>
          )}

          {canReplay && (
            <div
              className="flex items-center gap-1"
              role="group"
              aria-label="Reproduzir os últimos minutos"
            >
              <span className="text-xs text-fg-subtle">Últimos</span>
              {REPLAY_MINUTES.map((m) => (
                <BarButton
                  key={m}
                  label={`Reproduzir os últimos ${String(m)} minuto${m > 1 ? 's' : ''}`}
                  onClick={() => {
                    startReplay(Date.now() - m * 60_000);
                  }}
                >
                  {m} min
                </BarButton>
              ))}
            </div>
          )}
          {canReplay && <GoTo timeZone={timeZone} onGo={startReplay} />}

          {replay && (
            <div className="flex items-center gap-1" role="group" aria-label="Reprodução">
              <BarButton
                label="Voltar 10 segundos"
                onClick={() => {
                  adjust({ base: t - 10_000 });
                }}
              >
                <Rewind aria-hidden="true" className="size-3.5" />
              </BarButton>
              <BarButton
                label={replay.clock.playing ? 'Pausar' : 'Reproduzir'}
                onClick={() => {
                  adjust({ playing: !replay.clock.playing });
                }}
              >
                {replay.clock.playing ? (
                  <Pause aria-hidden="true" className="size-3.5" />
                ) : (
                  <Play aria-hidden="true" className="size-3.5" />
                )}
              </BarButton>
              <BarButton
                label="Avançar 10 segundos"
                onClick={() => {
                  adjust({ base: t + 10_000 });
                }}
              >
                <FastForward aria-hidden="true" className="size-3.5" />
              </BarButton>
              {origin === 'server' &&
                RATES.map((r) => (
                  <BarButton
                    key={r}
                    label={`Velocidade ${String(r)}×`}
                    pressed={replay.clock.rate === r}
                    onClick={() => {
                      adjust({ rate: r });
                    }}
                  >
                    {r}×
                  </BarButton>
                ))}
            </div>
          )}

          <span className="ml-auto" />
          {canReplay && (
            <BarButton
              label="Linha do tempo das gravações"
              pressed={showTimeline}
              onClick={() => {
                setShowTimeline((v) => !v);
              }}
            >
              <GanttChart aria-hidden="true" className="size-3.5" />
              Linha do tempo
            </BarButton>
          )}
          {fullscreenButton && (
            <BarButton
              label={fullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
              onClick={toggleFullscreen}
            >
              {fullscreen ? (
                <Shrink aria-hidden="true" className="size-3.5" />
              ) : (
                <Expand aria-hidden="true" className="size-3.5" />
              )}
              {fullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
            </BarButton>
          )}
        </div>
      </div>
    </div>
  );
}

/** DD/MM of instant t in the organisation's time zone. */
function dayLabel(t: number, timeZone: string | undefined): string {
  const [, m, d] = dayOf(t, timeZone).split('-');
  return `${d ?? ''}/${m ?? ''}`;
}

/** Replay from a chosen date and time (organisation time zone). */
function GoTo({ timeZone, onGo }: { timeZone: string | undefined; onGo: (t: number) => void }) {
  const [today] = useState(() => dayOf(Date.now(), timeZone));
  const [open, setOpen] = useState(false);
  const [day, setDay] = useState(today);
  const [time, setTime] = useState('');
  const [error, setError] = useState<string | null>(null);
  if (!open) {
    return (
      <BarButton
        label="Reproduzir a partir de uma data e hora"
        onClick={() => {
          setOpen(true);
        }}
      >
        Data e hora
      </BarButton>
    );
  }
  return (
    <form
      className="flex items-center gap-1"
      aria-label="Reproduzir a partir de"
      onSubmit={(e) => {
        e.preventDefault();
        const at = atWallClock(day, time, timeZone);
        if (at === null) {
          setError('Informe a hora.');
          return;
        }
        if (at >= Date.now() - LIVE_EDGE_MS) {
          setError('Escolha um horário no passado.');
          return;
        }
        setError(null);
        setOpen(false);
        onGo(at);
      }}
    >
      <label className="sr-only" htmlFor="wall-day">
        Data
      </label>
      <input
        id="wall-day"
        type="date"
        max={today}
        value={day}
        onChange={(e) => {
          setDay(e.target.value);
        }}
        className="h-7 rounded border border-line-strong bg-canvas px-1.5 text-xs text-fg"
      />
      <label className="sr-only" htmlFor="wall-time">
        Hora
      </label>
      <input
        id="wall-time"
        type="time"
        step={1}
        value={time}
        onChange={(e) => {
          setTime(e.target.value);
        }}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? 'wall-time-error' : undefined}
        className="h-7 rounded border border-line-strong bg-canvas px-1.5 font-mono text-xs text-fg aria-invalid:border-crit"
      />
      <BarButton label="Reproduzir deste horário" type="submit">
        Ir
      </BarButton>
      <BarButton
        label="Cancelar"
        onClick={() => {
          setOpen(false);
          setError(null);
        }}
      >
        Cancelar
      </BarButton>
      {error && (
        <span id="wall-time-error" role="alert" className="text-xs text-crit">
          {error}
        </span>
      )}
    </form>
  );
}

function BarButton({
  label,
  onClick,
  pressed,
  type = 'button',
  children,
}: {
  label: string;
  onClick?: () => void;
  pressed?: boolean;
  type?: 'button' | 'submit';
  children: ReactNode;
}) {
  return (
    <button
      type={type}
      aria-label={label}
      title={label}
      {...(pressed !== undefined ? { 'aria-pressed': pressed } : {})}
      onClick={onClick}
      className={`inline-flex h-7 items-center gap-1.5 rounded px-2 text-xs font-medium transition-colors ${
        pressed
          ? 'bg-accent-strong text-white'
          : 'text-fg-muted hover:bg-surface-hover hover:text-fg'
      }`}
    >
      {children}
    </button>
  );
}

function ModeButton({
  active,
  tone,
  title,
  onClick,
  children,
}: {
  active: boolean;
  tone: 'live' | 'replay';
  title?: string;
  onClick: () => void;
  children: ReactNode;
}) {
  const on =
    tone === 'live'
      ? 'bg-ok-soft text-ok ring-1 ring-ok/60'
      : 'bg-warn-soft text-warn ring-1 ring-warn/60';
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      title={title}
      onClick={onClick}
      className={`inline-flex h-7 items-center gap-1.5 rounded px-2.5 text-xs font-semibold transition-colors ${
        active ? on : 'text-fg-muted hover:bg-surface-hover hover:text-fg'
      }`}
    >
      {children}
    </button>
  );
}
